import React, { useState, useEffect } from 'react';

import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, Typography } from '@mui/material';

import { useUpdateTicketMutation } from 'services/api/tickets.api';
import type { Ticket } from 'services/types/ticket';

interface EditTicketDialogProps {
  open: boolean;
  onClose: () => void;
  ticket: Ticket;
  onSuccess?: () => void;
}

const EditTicketDialog: React.FC<EditTicketDialogProps> = ({ open, onClose, ticket, onSuccess }) => {
  const [title, setTitle] = useState(ticket.title);
  const [description, setDescription] = useState(ticket.description);
  const [updateTicket, { isLoading }] = useUpdateTicketMutation();

  useEffect(() => {
    if (open) {
      setTitle(ticket.title);
      setDescription(ticket.description);
    }
  }, [open, ticket]);

  const handleSubmit = () => {
    if (!title.trim() || !description.trim()) {
      return;
    }

    updateTicket({
      ticketId: ticket.id,
      data: {
        title: title.trim(),
        description: description.trim(),
      },
    })
      .then(() => {
        onSuccess?.();
        onClose();
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error updating ticket:', error);
      });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Editar Ticket</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField
            label="Título"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            error={!title.trim()}
            helperText={!title.trim() ? 'El título es requerido' : ''}
            fullWidth
            required
          />

          <TextField
            label="Descripción"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            error={!description.trim()}
            helperText={!description.trim() ? 'La descripción es requerida' : ''}
            multiline
            rows={4}
            fullWidth
            required
          />

          <Typography variant="caption" color="text.secondary">
            ID: {ticket.id} | Creado: {new Date(ticket.createdAt).toLocaleString('es-CL')}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancelar
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isLoading || !title.trim() || !description.trim()}>
          {isLoading ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

EditTicketDialog.defaultProps = {
  onSuccess: () => {},
};

export default EditTicketDialog;
